import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiFilter, FiInfo, FiSearch } from 'react-icons/fi';
import { useProducts } from '../hooks/useProducts';
import { useAdvancedSearch } from '../hooks/useAdvancedSearch';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useScrollReveal } from '../hooks/useGsapMotion';

const Search = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const { products, loading } = useProducts();
  const { searchProducts, getSpellingSuggestions } = useAdvancedSearch(products);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortBy, setSortBy] = useState('relevance');
  const [priceRange, setPriceRange] = useState('all');
  const [spellingSuggestions, setSpellingSuggestions] = useState([]);
  const pageRef = useRef(null);

  useScrollReveal(pageRef, [filteredProducts.length, sortBy, priceRange, query]);

  useEffect(() => {
    if (!query || !products.length) {
      setFilteredProducts([]);
      setSpellingSuggestions([]);
      return;
    }

    let filtered = searchProducts(query);

    if (filtered.length === 0) {
      setSpellingSuggestions(getSpellingSuggestions(query));
    } else {
      setSpellingSuggestions([]);
    }

    if (priceRange !== 'all') {
      const [min, max] = priceRange.split('-').map(Number);
      filtered = filtered.filter((product) => {
        const price = product.price;
        return max ? price >= min && price <= max : price >= min;
      });
    }

    if (sortBy === 'price-low') filtered.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-high') filtered.sort((a, b) => b.price - a.price);
    if (sortBy === 'name') filtered.sort((a, b) => a.name.localeCompare(b.name));

    setFilteredProducts(filtered);
  }, [query, products, sortBy, priceRange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div ref={pageRef} className="sc-container py-8 md:py-10">
      <section className="sc-soft-card mb-6 p-6" data-reveal>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-100 text-[#DB4444]">
            <FiSearch size={19} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-[#1A1A1A] md:text-3xl">Search Results</h1>
            <p className="text-sm text-slate-500">
              {query ? `Results for "${query}"` : 'Enter a search term to find products'}
            </p>
          </div>
        </div>

        {query && (
          <p className="mt-3 text-sm font-semibold text-slate-600">
            Found {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
          </p>
        )}

        {spellingSuggestions.length > 0 && (
          <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-3">
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-blue-800">
              <FiInfo size={15} />
              Did you mean
            </div>
            <div className="flex flex-wrap gap-2">
              {spellingSuggestions.map((suggestion, index) => (
                <Link
                  key={index}
                  to={`/search?q=${encodeURIComponent(suggestion)}`}
                  className="rounded-full bg-white px-3 py-1 text-xs font-bold text-blue-700 hover:bg-blue-100"
                >
                  {suggestion}
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {query ? (
        <>
          <section className="sc-soft-card mb-6 p-4" data-reveal>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="inline-flex items-center gap-2 text-sm font-bold text-slate-600">
                <FiFilter size={14} />
                Filters
              </div>
              <div className="flex flex-wrap gap-3">
                <select value={priceRange} onChange={(e) => setPriceRange(e.target.value)} className="sc-input min-w-[170px] text-sm">
                  <option value="all">All prices</option>
                  <option value="0-1000">৳0 - ৳1,000</option>
                  <option value="1000-5000">৳1,000 - ৳5,000</option>
                  <option value="5000-10000">৳5,000 - ৳10,000</option>
                  <option value="10000">৳10,000+</option>
                </select>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sc-input min-w-[170px] text-sm">
                  <option value="relevance">Relevance</option>
                  <option value="price-low">Price low to high</option>
                  <option value="price-high">Price high to low</option>
                  <option value="name">Name A-Z</option>
                </select>
              </div>
            </div>
          </section>

          {filteredProducts.length === 0 ? (
            <section className="sc-soft-card p-10 text-center" data-reveal>
              <h2 className="text-2xl font-black text-[#1A1A1A]">No products found</h2>
              <p className="mt-2 text-sm text-slate-500">
                We could not find products matching "{query}". Try another keyword.
              </p>
              <Link to="/products" className="sc-btn-primary mt-6 inline-block">
                Browse all products
              </Link>
            </section>
          ) : (
            <section className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4" data-stagger>
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </section>
          )}
        </>
      ) : (
        <section className="sc-soft-card p-10 text-center" data-reveal>
          <h2 className="text-2xl font-black text-[#1A1A1A]">Start your search</h2>
          <p className="mt-2 text-sm text-slate-500">Use the search field in the header to discover products fast.</p>
          <Link to="/products" className="sc-btn-primary mt-6 inline-block">
            Browse all products
          </Link>
        </section>
      )}
    </div>
  );
};

export default Search;
